import { SkeletonTable } from '@/components/table/SkeletonTable';
import { FlexCol } from '@/components/ui/flex';
import { useDebounce } from '@/hooks/useDebounce';
import { useSharedKnowledgeFilesService } from '@/services/sharedFiles';
import { SharedKnowledgeFileFilterParams } from '@ditto/shared';
import { useState, type FC } from 'react';
import { useColumns } from './FileTableColumns';
import { Filters } from './Filters';
import { SharedKnowledgeDataTable } from './SharedKnowledgeDataTable';

export const FileTable: FC<{ onUploadFiles: () => void }> = ({
  onUploadFiles,
}) => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [searchString, setSearchString] = useState<string>('');
  const columns = useColumns();

  const debouncedSearchString = useDebounce(searchString, 300);

  const filters: SharedKnowledgeFileFilterParams = {
    ...(selectedGroups.length > 0 && {
      groupIds: selectedGroups.join(','),
    }),
    ...(debouncedSearchString.length > 0 && {
      searchString: debouncedSearchString,
    }),
  };

  const { files, pagination, isLoading, error } =
    useSharedKnowledgeFilesService(page, limit, filters);

  const handleSelectedGroupsChange = (groups: string[]) => {
    setSelectedGroups(groups);
    setPage(1);
  };

  const handleSearchStringChange = (value: string) => {
    setSearchString(value);
    setPage(1);
  };

  const handleClearFilters = () => {
    setSearchString('');
    setSelectedGroups([]);
  };

  if (error) {
    return <div>Error loading files: {error.message}</div>;
  }

  return (
    <div className="w-full">
      <FlexCol gap="2">
        <Filters
          selectedGroups={selectedGroups}
          setSelectedGroups={handleSelectedGroupsChange}
          searchString={searchString}
          setSearchString={handleSearchStringChange}
        />

        {isLoading ? (
          <SkeletonTable columnLength={columns.length} />
        ) : (
          <SharedKnowledgeDataTable
            columns={columns}
            data={files}
            externalPagination={
              pagination
                ? {
                    page: pagination.page,
                    pageSize: pagination.limit,
                    totalPages: pagination.totalPages,
                    total: pagination.total,
                    hasNext: pagination.hasNext,
                    hasPrev: pagination.hasPrev,
                    onPageChange: setPage,
                    onPageSizeChange: setLimit,
                  }
                : undefined
            }
            onClearFilters={handleClearFilters}
            onUploadFiles={onUploadFiles}
          />
        )}
      </FlexCol>
    </div>
  );
};
